// src/ui/shell/parts.tsx: the pieces of the sidebar and the phone bar that the family's shell (ProspectShell) and the team's
// (TeamShell) share, so the two sides are one design: the builder's logo box, the project card, a nav row, the foot's account row.
// The classes are direction.css's (.side-brand, .proj, .nav-i, .me, .mtop-brand); neither shell draws its own.
import type { ComponentChildren } from "preact";
import { Link } from "../../app/router";
import { tenant } from "../../state/session";
import { Avatar, type AvatarTone } from "../Avatar";
import { cx } from "../cx";
import { Icon, type IconName } from "../Icon";
import { Logo } from "../Logo";

/** The builder's logo at the top of the sidebar, left-aligned in the same box on both sides. */
export function SideBrand(props: { to: string }) {
  const t = tenant.value;
  return (
    <Link class="side-brand" to={props.to} aria-label={t ? `${t.name}, home` : "Home"}>
      <Logo />
    </Link>
  );
}

/** The phone bar's brand: the builder's logo in the bar's box. */
export function BarBrand(props: { to: string }) {
  const t = tenant.value;
  return (
    <Link class="mtop-brand" to={props.to} aria-label={t ? `${t.name}, home` : "Home"}>
      <Logo slot="bar" class="logo" />
    </Link>
  );
}

/** The card under the logo: a small label, the name, one grey line. Static here; the family's switcher wraps it in a menu. */
export function ProjectCard(props: { kicker: string; title: string; loc?: string | null; children?: ComponentChildren }) {
  return (
    <>
      <span class="kicker">{props.kicker}</span>
      <b>{props.title}</b>
      {props.loc ? <span class="loc">{props.loc}</span> : null}
      {props.children}
    </>
  );
}

/** One sidebar row: 38px, the icon filled while current. */
export function NavItem(props: { to: string; icon: IconName; label: string; current: boolean; badge?: ComponentChildren }) {
  return (
    <Link class={cx("nav-i", props.current && "is-active")} to={props.to} aria-current={props.current ? "page" : undefined}>
      <Icon name={props.icon} filled={props.current} />
      {props.label}
      {props.badge}
    </Link>
  );
}

/** The foot's account row: the avatar, the name, "<role> · Settings", linking to Settings. */
export function MeRow(props: { to: string; current: boolean; initials: string; tone: AvatarTone; name: string; roleLabel: string }) {
  return (
    <Link class="me" to={props.to} aria-current={props.current ? "page" : undefined}>
      <Avatar initials={props.initials} tone={props.tone} size="sm" />
      <span>
        <b>{props.name}</b>
        <span class="me-sub">
          <span class="me-t">{props.roleLabel}</span>
          {/* The whole row opens Settings: a long role ("Operations Specialist") keeps the line, and the hint gives way. */}
          {props.roleLabel.length > 16 ? null : <span class="me-s"> · Settings</span>}
        </span>
      </span>
    </Link>
  );
}
